import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Typography,
  Spin,
  Space,
  Radio,
  Row,
  Col,
  Badge,
  message,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import masterService from "../../../services/masterService";
import ImageUpload from "../../../components/banners/ImageUpload";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";
import { useCommon } from "../../../context/CommonContext";

const { Title } = Typography;
const { Option } = Select;

const EditAvatar = () => {
  const { avatar_id } = useParams();
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [imageSource, setImageSource] = useState("upload"); // 'upload' | 'url'

  const fetchAvatarDetails = useCallback(async () => {
    if (!avatar_id) {
      message.error("Avatar ID is required");
      navigate("/avatar");
      return;
    }

    console.log("avatar_id", avatar_id);

    setLoadingData(true);
    try {
      const response = await masterService.getSingleAvatar(avatar_id);

      console.log("responseav", response);

      if (response.data && response.data.status) {
        const avatarData = response.data.data;
        if (avatarData && avatarData.avatar_id) {
          form.setFieldsValue({
            name: avatarData.name,
            image_url: avatarData.image_url,
            order_no: avatarData.order_no,
            is_active: avatarData.is_active ? "Y" : "N",
          });
        } else {
          notify.error({ message: "Avatar data not found" });
          navigate("/avatar");
        }
      } else {
        notify.error({
          message: response.data?.message || "Failed to fetch avatar details",
        });
        navigate("/avatar");
      }
    } catch (error) {
      console.error("Error fetching avatar details:", error);
      notify.error({ message: "Error fetching avatar details" });
      navigate("/avatar");
    } finally {
      setLoadingData(false);
    }
  }, [avatar_id, form, navigate]);

  useEffect(() => {
    setPageTitle("Edit Avatar");
    fetchAvatarDetails();
  }, [fetchAvatarDetails, setPageTitle]);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        name: values.name,
        image_url: values.image_url,
        order_no: Number(values.order_no),
        is_active: values.is_active === "Y" ? true : false,
      };

      const response = await masterService.updateAvatar(payload, avatar_id);

      if (response.data && response.data.status) {
        notify.success({
          message: response.data.message || "Avatar updated successfully",
        });
        navigate("/avatar");
      } else {
        notify.error({
          message: response.data?.message || "Failed to update avatar",
        });
      }
    } catch (error) {
      console.error("Error updating avatar:", error);
      notify.error({ message: "Error updating avatar" });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue({ is_active: "Y" });
  };

  const handleGoBack = () => {
    navigate("/avatar");
  };

  return (
    <div className="min-h-screen">
      <Spin spinning={loadingData}>
        <Card
          className="shadow-lg border-0"
          bodyStyle={{
            padding: "32px",
            backgroundColor: "#ffffff",
            borderColor: "#d9d9d9",
          }}
        >
          <Form
            form={form}
            layout="vertical"
            size="large"
            onFinish={handleSubmit}
            className="space-y-6"
          >
            {/* Basic Information Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <UserOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Avatar Information
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Avatar Name
                      </span>
                    }
                    name="name"
                    rules={[
                      { required: true, message: "Please enter avatar name" },
                      { validator: noWhitespaceValidator },
                    ]}
                  >
                    <Input
                      placeholder="Enter avatar name"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Order Number
                      </span>
                    }
                    name="order_no"
                    rules={[
                      { required: true, message: "Please enter order number" },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      placeholder="Enter order number"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Status
                      </span>
                    }
                    name="is_active"
                  >
                    <Select className="rounded-lg">
                      <Option value="Y">
                        <Badge status="success" text="Active" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="Inactive" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Image Configuration Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <PictureOutlined />
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Image Configuration
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Avatar Image
                      </span>
                    }
                  >
                    <Radio.Group
                      value={imageSource}
                      onChange={(e) => setImageSource(e.target.value)}
                      style={{ marginBottom: 16 }}
                    >
                      <Radio.Button value="upload">Upload Image</Radio.Button>
                      <Radio.Button value="url">Use Image URL</Radio.Button>
                    </Radio.Group>
                    {imageSource === "upload" ? (
                      <ImageUpload
                        label=""
                        name="image_url"
                        width={256}
                        height={256}
                        imageType="avatars"
                        required
                        value={form.getFieldValue("image_url")}
                        onChange={(val) =>
                          form.setFieldsValue({ image_url: val })
                        }
                      />
                    ) : (
                      <Form.Item
                        name="image_url"
                        rules={[
                          { required: true, message: "Please enter image URL" },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input
                          placeholder="Enter image URL"
                          className="rounded-lg"
                        />
                      </Form.Item>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Action Buttons */}
            <div
              className="flex justify-end gap-4 pt-6 border-t"
              style={{ borderColor: "#d9d9d9" }}
            >
              <Space size="middle">
                <Button
                  onClick={handleReset}
                  icon={<ReloadOutlined />}
                  size="large"
                  className="px-6"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleGoBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SaveOutlined />}
                  size="large"
                  className="px-8"
                >
                  {loading ? "Updating..." : "Update Avatar"}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </Spin>
    </div>
  );
};

export default EditAvatar;
